import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { accountingService } from '../../services/accountingService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { PlusIcon, CheckCircleIcon, EyeIcon, BookOpenIcon } from '@heroicons/react/24/outline';

const JournalEntries = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [formData, setFormData] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    description: '',
    entries: [{ account_id: '', debit: '', credit: '', description: '' }]
  });

  const { data, isLoading } = useQuery(
    'journal-entries',
    () => accountingService.getJournalEntries()
  );

  const { data: accountsData } = useQuery(
    'chart-of-accounts-list',
    () => accountingService.getChartOfAccounts()
  );

  const createMutation = useMutation(
    (entryData) => accountingService.createJournalEntry(entryData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('journal-entries');
        setShowModal(false);
        resetForm();
        toast.success('Journal entry created successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Creation failed');
      }
    }
  );

  const postMutation = useMutation(
    (id) => accountingService.postJournalEntry(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('journal-entries');
        toast.success('Journal entry posted successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Posting failed');
      }
    }
  );

  const resetForm = () => {
    setFormData({
      entry_date: new Date().toISOString().split('T')[0],
      description: '',
      entries: [{ account_id: '', debit: '', credit: '', description: '' }]
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate debit = credit
    const totalDebit = formData.entries.reduce((sum, e) => sum + (parseFloat(e.debit) || 0), 0);
    const totalCredit = formData.entries.reduce((sum, e) => sum + (parseFloat(e.credit) || 0), 0);

    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      toast.error(`Total debit (${totalDebit}) does not equal total credit (${totalCredit})`);
      return;
    }

    const submitData = {
      entry_date: formData.entry_date,
      description: formData.description,
      entries: formData.entries.map(e => ({
        account_id: parseInt(e.account_id),
        debit: parseFloat(e.debit) || 0,
        credit: parseFloat(e.credit) || 0,
        description: e.description
      }))
    };

    createMutation.mutate(submitData);
  };

  const addEntryLine = () => {
    setFormData({
      ...formData,
      entries: [...formData.entries, { account_id: '', debit: '', credit: '', description: '' }]
    });
  };

  const removeEntryLine = (index) => {
    const newEntries = formData.entries.filter((_, i) => i !== index);
    setFormData({ ...formData, entries: newEntries });
  };

  const updateEntryLine = (index, field, value) => {
    const newEntries = [...formData.entries];
    newEntries[index][field] = value;
    setFormData({ ...formData, entries: newEntries });
  };

  const accounts = accountsData?.data?.accounts || [];
  const entries = data?.data || [];

  const totalDebit = formData.entries.reduce((sum, e) => sum + (parseFloat(e.debit) || 0), 0);
  const totalCredit = formData.entries.reduce((sum, e) => sum + (parseFloat(e.credit) || 0), 0);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold mb-2">Journal Entries</h2>
          <p className="text-gray-600">Create and manage journal vouchers</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Journal Entry
        </button>
      </div>

      {/* Journal Entries List */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <BookOpenIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>No journal entries found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Voucher No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Debit</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Credit</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {entry.voucher_no}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(entry.entry_date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                    {entry.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                    {formatCurrency(entry.total_debit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600">
                    {formatCurrency(entry.total_credit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      entry.status === 'posted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {entry.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center space-x-2">
                    <button
                      onClick={() => setSelectedEntry(entry)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <EyeIcon className="h-5 w-5 inline" />
                    </button>
                    {entry.status === 'draft' && (
                      <button
                        onClick={() => {
                          if (window.confirm('Post this journal entry? This will update account balances.')) {
                            postMutation.mutate(entry.id);
                          }
                        }}
                        className="text-green-600 hover:text-green-800"
                      >
                        <CheckCircleIcon className="h-5 w-5 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Journal Entry Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 my-8 max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Journal Entry</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Entry Date</label>
                  <input
                    type="date"
                    value={formData.entry_date}
                    onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Journal entry description..."
                    required
                  />
                </div>
              </div>

              {/* Journal Entry Lines */}
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Account</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Debit</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Credit</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {formData.entries.map((entry, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2">
                          <select
                            value={entry.account_id}
                            onChange={(e) => updateEntryLine(index, 'account_id', e.target.value)}
                            className="w-48 px-2 py-1 border rounded text-sm"
                            required
                          >
                            <option value="">Select Account</option>
                            {accounts.map(account => (
                              <option key={account.id} value={account.id}>
                                {account.account_code} - {account.account_name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={entry.debit}
                            onChange={(e) => updateEntryLine(index, 'debit', e.target.value)}
                            className="w-32 px-2 py-1 border rounded text-right"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            step="0.01"
                            value={entry.credit}
                            onChange={(e) => updateEntryLine(index, 'credit', e.target.value)}
                            className="w-32 px-2 py-1 border rounded text-right"
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={entry.description}
                            onChange={(e) => updateEntryLine(index, 'description', e.target.value)}
                            className="w-64 px-2 py-1 border rounded text-sm"
                            placeholder="Line description"
                          />
                        </td>
                        <td className="px-4 py-2 text-center">
                          {formData.entries.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeEntryLine(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="4" className="px-4 py-2 text-right font-bold">
                        Totals:
                      </td>
                      <td className="px-4 py-2 text-right font-bold">
                        {formatCurrency(totalDebit)} / {formatCurrency(totalCredit)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <button
                type="button"
                onClick={addEntryLine}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                + Add Line
              </button>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {createMutation.isLoading ? 'Creating...' : 'Create Journal Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Entry Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Journal Entry Details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-500">Voucher No:</span>
                  <div className="font-mono">{selectedEntry.voucher_no}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Date:</span>
                  <div>{formatDate(selectedEntry.entry_date)}</div>
                </div>
                <div className="col-span-2">
                  <span className="text-sm text-gray-500">Description:</span>
                  <div>{selectedEntry.description}</div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Account</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Debit</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedEntry.entries || []).map((entry, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 text-sm">{entry.account_name}</td>
                        <td className="px-4 py-2 text-right text-sm">{formatCurrency(entry.debit)}</td>
                        <td className="px-4 py-2 text-right text-sm">{formatCurrency(entry.credit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setSelectedEntry(null)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalEntries;